import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Eye, Loader } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import StatCard from "../../components/dashboard/StatCard";
import RecentOrders from "../../components/dashboard/RecentOrders";
import LowStockAlert from "../../components/dashboard/LowStockAlert";
import { useDashboard } from "../../hooks/useDashboard";
import formatCurrency from "../../utils/formatCurrency";

const MainDashboard = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { dashboardData, loading, error } = useDashboard(page);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > (dashboardData?.recent_orders?.last_page || 1))
      return;
    setPage(newPage);
  };

  // Prepare data for Recharts
  const chartData =
    dashboardData?.sales_report?.months?.map((month, index) => ({
      name: month,
      sales: dashboardData?.sales_report?.counts?.[index] || 0,
    })) || [];

  if (loading) {
    return (
      <div className="p-8 bg-white min-h-screen flex items-center justify-center">
        <Loader size={32} className="text-[#6B7143] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 bg-white min-h-screen">
        <div className="bg-red-50 border border-red-200 text-red-600 px-6 py-4 rounded-lg text-sm">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 md:p-8 bg-white min-h-screen font-sans">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-8">
        <div>
          <h1 className="text-xl font-medium text-[#0C0C0E]">
            Dashboard Overview
          </h1>
          <p className="font-medium text-sm text-[#7D828D]">
            Welcome back! Here's what's happening with your store today.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 text-sm w-full sm:w-auto">
          <button
            onClick={() => navigate("/product/add")}
            className="flex items-center gap-2 bg-[#D2BDA1] text-black px-4 py-2 rounded-lg font-medium hover:bg-[#c5b49f] transition-colors active:scale-95 active:shadow-sm duration-100"
          >
            <Plus size={18} /> Add Product
          </button>
          <button
            onClick={() => navigate("/orders")}
            className="flex items-center gap-2 bg-gradient-to-b from-[#FFFFFF] to-[#F3F3F3] border border-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-50 transition-colors active:scale-95 active:shadow-sm duration-100"
          >
            <Eye size={18} className="text-[#AAADB5]" /> View Orders
          </button>
        </div>
      </div>

      {/* Stats Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Total Sales"
          value={formatCurrency(dashboardData?.total_sales)}
        />
        <StatCard label="Orders" value={dashboardData?.orders_count || 0} />
        <StatCard
          label="Customers"
          value={dashboardData?.customers_count || 0}
        />
        <StatCard label="Products" value={dashboardData?.products_count || 0} />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Monthly Sales Chart */}
        <div className="lg:col-span-2 bg-white p-4 sm:p-6 border border-gray-100 rounded-xl shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-bold text-gray-900">Monthly Sales</h3>
          </div>

          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{ top: 10, right: 10, left: 20, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#F3F4F6"
                />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "black", fontSize: 14 }}
                  dy={10}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "black", fontSize: 14 }}
                  tickFormatter={(value) =>
                    `₦${value >= 1000 ? value / 1000 + "k" : value}`
                  }
                />
                <Tooltip
                  cursor={{ fill: "#F9FAFB" }}
                  contentStyle={{
                    borderRadius: "8px",
                    border: "none",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  }}
                  formatter={(value) => [formatCurrency(value), "Sales"]}
                />
                <Bar dataKey="sales" radius={[6, 6, 0, 0]} barSize={32}>
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        index === chartData.length - 1 ? "#D2BDA1" : "#E5DED4"
                      }
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Low Stock Alert */}
        <LowStockAlert products={dashboardData?.product_low_stock || []} />
      </div>

      <RecentOrders
        orders={dashboardData?.recent_orders?.data || []}
        pagination={dashboardData?.recent_orders}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default MainDashboard;
